// component/dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';

import { FaCalendarPlus, FaUsers, FaClock, FaShareAlt, FaCalendarDay, FaCalendarCheck, FaCalendarWeek, FaQrcode, FaPrint, FaRegClock, FaUser } from 'react-icons/fa';
import apiService from '../../services/ApiServices';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userLink, setUserLink] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');

  const formatDuration = (duration) => {
    if (!duration) return 'Not set';
    try {
      const dur = JSON.parse(duration);
      return dur.normalvalue === 'custom' ? dur.customvalue.join(' ') : dur.normalvalue;
    } catch {
      return `${duration} min`;
    }
  };

  const showAlertMessage = (message, type = 'info') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4000);
  };

  const handlePrintQr = (url, title, eventObj = null) => {
    const qrSvg = renderToStaticMarkup(
      <QRCodeSVG value={url} size={200} level="H" />
    );
    const printWindow = window.open('', '_blank', 'width=600,height=500');
    if (!printWindow) {
      showAlertMessage('Pop-up blocked! Please allow pop-ups to print the QR Code.', 'danger');
      return;
    }
    const durationText = eventObj ? formatDuration(eventObj.duration) : '';
    const typeText = '';
    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #fff;
            }
            .print-container {
              text-align: center;
              padding: 2rem;
            }
            .print-title {
              font-size: 1.4rem;
              font-weight: 700;
              color: #1a1a2e;
              margin-bottom: 0.5rem;
            }
            .print-meta {
              font-size: 0.85rem;
              color: #6c757d;
              margin-bottom: 1.25rem;
            }
            .qr-wrapper {
              display: inline-block;
              padding: 1rem;
              border: 2px solid #e9ecef;
              border-radius: 12px;
              margin-bottom: 1rem;
            }
            .print-url {
              font-size: 0.7rem;
              color: #adb5bd;
              word-break: break-all;
              max-width: 320px;
              margin: 0 auto;
            }
            @media print {
              body { min-height: auto; }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="print-title">${title || 'QR Code'}</div>
            ${durationText || typeText ? '<div class="print-meta">' + (typeText ? typeText : '') + (typeText && durationText ? ' &bull; ' : '') + (durationText ? durationText : '') + '</div>' : ''}
            <div class="qr-wrapper">${qrSvg}</div>
            <div class="print-url">${url}</div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              window.close();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await apiService.get('/api/meeting/dashboard-stats');
        setUserLink(response.data.user_link || '');
        setStats(response.data || { yesterday: 0, todayMeetings: 0, currentWeekMeetings: 0 });
      } catch (err) {
        console.error('Failed to fetch dashboard stats:', err);
        setStats({ yesterday: 0, todayMeetings: 0, currentWeekMeetings: 0 });
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const quickActions = [
    { icon: <FaCalendarPlus />, label: 'Schedule Meeting', link: '/events/new' },
    { icon: <FaUsers />, label: 'View Meetings', link: '/meetings' },
    { icon: <FaShareAlt />, label: 'Share Availability', link: '/availability' }
  ];

  const statCards = stats ? [
    { icon: <FaCalendarDay />, value: stats.yesterday, label: 'Yesterday', color: 'secondary', link: '/meetings' },
    { icon: <FaClock />, value: stats.todayMeetings, label: 'Today', color: 'primary', link: '/meetings' },
    { icon: <FaCalendarWeek />, value: stats.currentWeekMeetings, label: 'This Week', color: 'success', link: '/meetings' }
  ] : [];

  const qrUrl = userLink ? `${window.location.origin}/booking/${userLink}` : '';

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 fw-bold text-dark mb-1">Dashboard</h1>
              <p className="text-muted mb-0">Welcome back! Here's your meeting overview.</p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        {loading ? (
          <Col className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" className="me-2" />
            <span className="text-muted">Loading stats...</span>
          </Col>
        ) : (
          statCards.map((stat, index) => (
            <Col key={index} lg={4} md={6} className="mb-3">
              <Link to={stat.link} className="text-decoration-none">
                <Card className="modern-card border-0 h-100 shadow-sm" style={{ cursor: 'pointer' }}>
                  <Card.Body className="p-4">
                    <div className={`d-flex align-items-center mb-3 text-${stat.color}`}>
                      <div className="fs-2">{stat.icon}</div>
                    </div>
                    <h3 className="fw-bold text-dark mb-1">{stat.value}</h3>
                    <p className="text-muted mb-0">{stat.label}</p>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          ))
        )}
      </Row>

      {/* Actions and QR Section */}
      <Row>

        {/* QR Code Section */}
        <Col lg={4} className="mb-3">
          <Card className="modern-card border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 pt-4 pb-0">
              <h5 className="fw-bold mb-0 d-flex align-items-center">
                <FaQrcode className="me-2 text-primary" /> Booking QR Code
              </h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column align-items-center justify-content-center">
              {loading || !userLink ? (
                <div className="text-center">
                  <Spinner animation="border" variant="secondary" size="sm" className="mb-2" />
                  <br />
                  <span className="text-muted">Generating QR...</span>
                </div>
              ) : (
                <>
                  <div className="mb-3 p-3 border rounded-3 bg-light d-flex align-items-center justify-content-center" style={{ minHeight: '200px' }}>
                    <QRCodeSVG value={qrUrl} size={160} level="H" />
                  </div>
                  <small className="text-muted text-break text-center font-monospace mb-3" style={{ fontSize: '0.75rem', maxWidth: '100%' }}>
                    {qrUrl}
                  </small>
                  <Button
                    variant="primary"
                    className="d-flex align-items-center w-100 justify-content-center"
                    onClick={() => handlePrintQr(qrUrl, 'Book Your Meetings')}
                  >
                    <FaPrint className="me-2" /> Print QR Code
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        {/* Quick Actions */}
        <Col lg={8} className="mb-3">
          <Card className="modern-card border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-0 pt-4 pb-0">
              <h5 className="fw-bold mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                {quickActions.map((action, index) => (
                  <Col key={index} md={4} className="mb-3">
                    <Button
                      as={Link}
                      to={action.link}
                      variant="outline-primary"
                      className="w-100 h-100 p-3 d-flex flex-column align-items-center justify-content-center border-dashed text-decoration-none"
                      style={{ minHeight: '120px', border: '2px dashed #dee2e6' }}
                    >
                      <div className="fs-2 text-primary mb-2">{action.icon}</div>
                      <span className="fw-semibold">{action.label}</span>
                    </Button>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>


      </Row>
    </Container>
  );
};

export default Dashboard;